import { MapProperties } from '@datum-group/datum-map/lib';
import { Services } from '@project/API';
import { getFeaturesLegends } from '@project/components/Module/Cartograms';
import {
  expandExtentByPixels,
  getFeaturesExtent,
  getMapScreenshot,
} from '@project/utils/map';
import { autorun, makeObservable, observable, reaction } from 'mobx';
import { CartogramFeaturesStore } from './CartogramFeatures';
import { CartogramFiltersStore } from './CartogramFilters';
import { CartogramPdfManager } from './CartogramPdfManager';
import { CartogramsMapControllerStore } from './CartogramsMapController';
import { LegendsSettingsStore } from './LegendSettingsStore';

export class CartogramStore {
  mapProperties: MapProperties;
  mapController: CartogramsMapControllerStore;
  @observable pdfManager: CartogramPdfManager;
  @observable features: CartogramFeaturesStore;
  @observable filters: CartogramFiltersStore;
  @observable legendsSettings: LegendsSettingsStore;

  private subscribesToDispose: (() => void)[] = [];

  constructor(services: Services, mapProperties: MapProperties) {
    this.mapProperties = mapProperties;
    this.mapController = new CartogramsMapControllerStore(mapProperties);
    this.pdfManager = new CartogramPdfManager();
    this.features = new CartogramFeaturesStore(services);
    this.filters = new CartogramFiltersStore(services);
    this.legendsSettings = new LegendsSettingsStore();
    makeObservable(this);
  }

  bind = () => {
    this.mapController.createLayer();
    this.mapController.setStyle();

    const disposeFeaturesFetch = reaction(
      () => this.filters.cartogram,
      () => {
        if (!this.filters.cartogram) return this.features.clear();

        return this.features.load(this.filters.cartogram);
      },
    );

    const disposeFeaturesFill = reaction(
      () => this.features.data,
      () => this.mapController.setFeatures(this.features.data),
    );

    const disposeFiltersChange = autorun(() => {
      if (!this.filters.tableColumn) {
        this.legendsSettings.reset();
        this.mapController.setStyle();

        return undefined;
      }

      return this.mapController.setStyleFunction({
        legendsSettings: this.legendsSettings.settings,
        columnName: this.filters.tableColumn,
        displayedText: `{${this.filters.tableColumn}}`,
      });
    });

    this.subscribesToDispose = [
      disposeFeaturesFetch,
      disposeFeaturesFill,
      disposeFiltersChange,
    ];

    this.filters.bind();
  };

  unbind = () => {
    this.subscribesToDispose?.forEach((dispose) => dispose());
    this.mapController.removeCartogramLayer();

    this.filters.unbind();
  };

  getCartogramScreenshot = async () => {
    const features = this.mapController.getFeatures();

    const screenshotExtent = expandExtentByPixels(
      this.mapProperties.map,
      getFeaturesExtent(features),
      50,
    );

    return getMapScreenshot(this.mapProperties.map, screenshotExtent);
  };

  generatePdf = async () => {
    const features = this.mapController.getFeatures();
    const computeColumn = this.filters.tableColumn;
    const cartogramTable = this.filters.cartogramTable;

    if (!computeColumn || !cartogramTable) return;

    const title = cartogramTable.description ?? cartogramTable.table_name;

    const featuresLegends = getFeaturesLegends(
      features,
      this.legendsSettings.settings,
      computeColumn,
      this.mapProperties.map,
    );

    const screenshot = await this.getCartogramScreenshot();

    return this.pdfManager.createPdf({
      title,
      screenshot,
      features,
      featuresLegends,
      legendsSettings: this.legendsSettings.settings,
    });
  };
}
